// The OAs Interview Prep plans around: every card at OA, with its due date
// (oaDue, the same as its Home to-do), unless the user said "I'll prep myself".
// A card that moved on from OA (an interview, a rejection) stays for a week so
// its debrief can still be asked. What the user told us about each one lives at
// users/{uid}/prepEvents/{cardId}.

import { toStatus, type ApiItem } from "./applicationCards";
import { oaDoneAt, oaDue } from "./home";
import type { OaKind, PrepEvent } from "./prepPlanner";
import { addDays, daysBetween, type SkillEvent } from "./prepSkills";
import type { CodingTopicId } from "../data/prep/topics";

export type OaWent = "well" | "ok" | "rough";

/** The three taps after an OA */
export type Debrief = {
  went: OaWent;
  /** Where they got stuck; each one moves that topic down */
  topics: CodingTopicId[];
  /** A build-in-stages OA: the last level they finished (0 to 4) */
  levels?: number;
  note?: string;
  /**
   * What they were asked, in their words, with the card's company and role so
   * they can later help others interviewing there (kept private for now)
   */
  questions?: string[];
  company?: string;
  role?: string;
  /** Shared with others interviewing at the same company (companyDebriefs) */
  shared?: boolean;
  /** yyyy-mm-dd it was written */
  day: string;
};

/** A debrief's questions, cleaned: trimmed, no blanks or repeats */
export function cleanQuestions(questions: string[]): string[] {
  const seen = new Set<string>();
  return questions
    .map((q) => q.trim().replace(/\s+/g, " "))
    .filter((q) => q && !seen.has(q.toLowerCase()) && seen.add(q.toLowerCase()));
}

export type PrepEventDoc = {
  /** "I'll prep myself": no prep planned for it */
  selfPrep?: boolean;
  /** Picked by the user; otherwise it's read from the emails */
  kind?: OaKind;
  debrief?: Debrief;
  /** Before debriefs had answers: the debrief item was just checked off */
  debriefed?: boolean;
};

/** A card that moved on from OA is asked for a debrief this many days after */
export const DEBRIEF_DAYS = 7;

/** What kind of OA a card's emails say it is; "other" when they don't say */
export function detectOaKind(card: ApiItem): OaKind {
  const text = [card.body ?? "", ...(card.emails ?? []).map((e) => e.subject)].join("\n");
  if (/industry coding (?:framework|assessment|skills)|\bICF\b/i.test(text)) return "icf";
  if (/general coding assessment|\bGCA\b/i.test(text)) return "gca";
  return "other";
}

// Interview Prep's OA prep and debrief are for coding tests (LeetCode-style).
const CODING_OA = /hacker ?rank|code ?signal|leetcode|codility|coderpad|karat|\bcoding\b|programming|algorithm|data structures|technical (?:assessment|challenge|test|screen)|take[- ]home|\bGCA\b|\bICF\b|industry coding/i;
// Other steps that come in as an OA: a form, a video interview, a reasoning or personality test
const OTHER_STEP = /\bforms?\b|questionnaire|survey|video (?:interview|assessment|response)|hirevue|one[- ]way interview|personality|situational judg|(?:numerical|verbal|logical) reasoning|cognitive|aptitude|game[- ]based|pymetrics|work ?style/i;
const TECH_ROLE = /software|developer|\bSWE\b|\bSDE\b|programmer|data scien|machine learning|\bML\b|\bAI\b|back[- ]?end|front[- ]?end|full[- ]?stack|devops|\bSRE\b|site reliability|platform engineer|infrastructure|mobile|\biOS\b|android|quant/i;

/**
 * Whether a card's OA is a coding test: its emails say so, or it's for a
 * technical role (or one we don't know) and they don't say it's another kind of step. A form or a
 * business role's assessment gets no coding prep or debrief.
 */
export function isCodingOa(card: ApiItem): boolean {
  const text = [card.body ?? "", ...(card.emails ?? []).map((e) => e.subject)].join("\n");
  if (CODING_OA.test(text)) return true;
  if (OTHER_STEP.test(text)) return false;
  // No role on the card: as before, assume coding
  const role = card.role?.trim();
  return !role || TECH_ROLE.test(role);
}

/** Local yyyy-mm-dd of an ISO time */
const localDay = (iso: string) => {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * The day a card moved on from OA (to an interview, offer, rejection or
 * ghosting), or null if it never did. A move back to Applied undoes the OA
 * (an email read as one that wasn't), so there's nothing to debrief.
 */
export function leftOaOn(card: ApiItem): string | null {
  const history = card.history ?? [];
  const lastOa = history.map((e) => e.stage).lastIndexOf("oa");
  const next = lastOa >= 0 ? history[lastOa + 1] : undefined;
  if (!next || next.stage === "submitted") return null;
  return Date.parse(next.at) ? localDay(next.at) : null;
}

/**
 * The day the card's current OA was finished, from an "assessment completed"
 * email (oaCompletedAt), or null. One from before the card's last move to OA
 * was for an earlier OA.
 */
export function finishedOn(card: ApiItem): string | null {
  const at = oaDoneAt(card);
  return at ? localDay(new Date(at).toISOString()) : null;
}

const cardName = (card: ApiItem) => (card.company && card.company !== "Unknown" ? card.company : "This");

/** The OAs to plan around, from the user's cards and what they said about each */
export function oaEvents(cards: ApiItem[], docs: Record<string, PrepEventDoc>, now = new Date()): PrepEvent[] {
  const events: PrepEvent[] = [];
  const today = localDay(now.toISOString());
  for (const card of cards) {
    const doc = docs[card.id] ?? {};
    if (doc.selfPrep || !isCodingOa(card)) continue;
    const kind = doc.kind ?? detectOaKind(card);
    const debriefed = Boolean(doc.debrief || doc.debriefed);
    if (toStatus(card.predicted_label) === "oa") {
      // Finished (an "assessment completed" email): its prep stops, and the debrief comes the next day
      const finished = finishedOn(card);
      if (finished) {
        if (debriefed || daysBetween(finished, today) > DEBRIEF_DAYS) continue;
        events.push({ id: card.id, company: cardName(card), due: finished, dueKnown: true, takenOn: finished, kind });
        continue;
      }
      const due = oaDue(card, now);
      if (!due) continue;
      events.push({ id: card.id, company: cardName(card), due: due.due, dueKnown: due.known, kind, ...(debriefed ? { debriefed } : {}) });
      continue;
    }
    // Moved on from OA lately and not debriefed: it was taken by the day before
    const left = leftOaOn(card);
    if (!left || debriefed || daysBetween(left, today) > DEBRIEF_DAYS) continue;
    const taken = addDays(left, -1);
    events.push({ id: card.id, company: cardName(card), due: taken, dueKnown: true, takenOn: taken, kind });
  }
  return events.sort((a, b) => a.due.localeCompare(b.due));
}

/** Debriefs as skill events: each topic they got stuck on moves down */
export function debriefEvents(docs: Record<string, PrepEventDoc>): SkillEvent[] {
  return Object.values(docs).flatMap((doc) =>
    doc.debrief ? doc.debrief.topics.map((topic): SkillEvent => ({ kind: "struggled", day: doc.debrief!.day, topic })) : []
  );
}
